import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react"
import { Link } from "react-router-dom"
import { api } from "../../lib/api"
import type { HabilitacaoComProvas, PlanoDeEstudo } from "../../types"
import { Botao } from "../ui"

/**
 * O plano de estudo até a prova — bônus da preparação completa. A pessoa diz
 * a data; o servidor monta o dia a dia a partir do histórico dela
 * (Estudo::Plano), e recalcula a cada visita: se ela estudou, as unidades
 * fracas mudam e o plano muda junto.
 *
 * Só para quem tem acesso vigente e o bônus (`liberado`). Sem data, o card é
 * a pergunta "quando é a sua prova?"; com data, hoje em destaque e a semana.
 */
export default function Plano({ habilitacoes, recarregar }: { habilitacoes: HabilitacaoComProvas[]; recarregar: () => Promise<void> }) {
  // A prova que a pessoa vai fazer: o Arrais, quando ela o tem (quem tem a
  // preparação completa faz uma prova só, a de Arrais).
  const alvo = habilitacoes.find((h) => h.acesso.vigente && h.slug === "arrais-amador") ?? habilitacoes.find((h) => h.acesso.vigente)
  const [plano, setPlano] = useState<PlanoDeEstudo | null>(null)
  const [editando, setEditando] = useState(false)

  const carregar = useCallback(() => {
    if (!alvo) return
    api
      .get<PlanoDeEstudo>(`/plano?habilitacao=${encodeURIComponent(alvo.slug)}`)
      .then(setPlano)
      .catch(() => setPlano(null))
  }, [alvo])

  useEffect(() => {
    carregar()
  }, [carregar])

  if (!alvo || !plano || !plano.liberado) return null

  const semData = plano.data_da_prova === null || plano.dias.length === 0

  async function salvarData(data: string) {
    await api.patch("/me", { usuario: { data_da_prova: data } })
    setEditando(false)
    await recarregar()
    carregar()
  }

  return (
    <section aria-label="Seu plano de estudo" className="space-y-4">
      <h2
        className="revela font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600"
        style={{ "--atraso": "200ms" } as CSSProperties}
      >
        Seu plano até a prova
      </h2>

      {semData || editando ? (
        <PedidoDeData
          atual={plano.data_da_prova}
          passada={plano.data_da_prova !== null && plano.dias.length === 0}
          aoSalvar={salvarData}
          aoCancelar={editando ? () => setEditando(false) : undefined}
        />
      ) : (
        <div className="revela rounded-2xl border border-navy-100 bg-white p-6 shadow-sm sm:p-7" style={{ "--atraso": "240ms" } as CSSProperties}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-slate-600">
              Prova de {alvo.nome} em <strong className="font-semibold text-navy-900">{dataLonga(plano.data_da_prova!)}</strong>
              {plano.dias_restantes !== null && plano.dias_restantes > 0 && (
                <> · {plano.dias_restantes === 1 ? "falta 1 dia" : `faltam ${plano.dias_restantes} dias`}</>
              )}
            </p>
            <button type="button" onClick={() => setEditando(true)} className="text-xs font-semibold text-navy-700 underline underline-offset-4">
              mudar a data
            </button>
          </div>

          {/* Hoje, em destaque: é a única coisa que a pessoa precisa decidir. */}
          <div className="mt-4 rounded-xl border border-gold-500/60 bg-gold-500/10 p-4">
            <p className="font-mono text-[0.62rem] uppercase tracking-wider text-gold-700">Hoje · {dataCurta(plano.dias[0].data)}</p>
            <p className="mt-1 font-semibold text-navy-900">{plano.dias[0].titulo}</p>
            <p className="mt-1 text-sm text-slate-700">{plano.dias[0].descricao}</p>
            <div className="mt-3">
              <AcaoDoDia tipo={plano.dias[0].tipo} slug={alvo.slug} />
            </div>
          </div>

          {plano.dias.length > 1 && (
            <ol className="mt-4 divide-y divide-navy-50">
              {plano.dias.slice(1, 7).map((dia) => (
                <li key={dia.data} className="flex gap-3 py-2 text-sm">
                  <span className="w-20 flex-none font-mono text-[0.7rem] uppercase text-navy-400">{diaDaSemana(dia.data)} {dataCurta(dia.data)}</span>
                  <span className="text-navy-900">{dia.titulo}</span>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-3 text-xs text-slate-500">
            O plano é refeito a cada dia a partir do seu histórico — as unidades mais fracas vêm primeiro. A tarefa de cada
            manhã também chega por e-mail.
          </p>
        </div>
      )}
    </section>
  )
}

/** O que o dia pede, como ação: começar o simulado, abrir o histórico, abrir os guias. */
function AcaoDoDia({ tipo, slug }: { tipo: string; slug: string }) {
  if (tipo === "simulado") {
    return (
      <Link to={`/painel?habilitacao=${slug}`} className="text-sm font-semibold text-navy-800 underline underline-offset-4">
        Começar o simulado no formato da prova ↑
      </Link>
    )
  }
  if (tipo === "unidade" || tipo === "revisao") {
    return (
      <Link to="/historico" className="text-sm font-semibold text-navy-800 underline underline-offset-4">
        Abrir o histórico e rever os erros
      </Link>
    )
  }
  return null
}

function PedidoDeData({
  atual,
  passada,
  aoSalvar,
  aoCancelar,
}: {
  atual: string | null
  passada: boolean
  aoSalvar: (data: string) => Promise<void>
  aoCancelar?: () => void
}) {
  const [data, setData] = useState(atual ?? "")
  const [salvando, setSalvando] = useState(false)
  const hoje = new Date().toISOString().slice(0, 10)

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!data) return
    setSalvando(true)
    try {
      await aoSalvar(data)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <form onSubmit={enviar} className="revela rounded-2xl border border-navy-100 bg-white p-6 shadow-sm sm:p-7" style={{ "--atraso": "240ms" } as CSSProperties}>
      <p className="font-semibold text-navy-900">{passada ? "Sua prova já passou. Vai fazer de novo? Diga a nova data." : "Quando é a sua prova?"}</p>
      <p className="mt-1 text-sm text-slate-600">
        Com a data, o painel monta o seu plano dia a dia — o que estudar hoje, quando fazer o simulado, o que revisar na
        véspera — e manda a tarefa de cada manhã por e-mail. Ainda não marcou? Use a data em que pretende fazer.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          type="date"
          value={data}
          min={hoje}
          onChange={(e) => setData(e.target.value)}
          className="rounded-xl border border-navy-200 px-3 py-2.5 text-sm"
          aria-label="Data da prova"
        />
        <Botao type="submit" disabled={salvando || !data}>
          {salvando ? "Montando…" : "Montar meu plano"}
        </Botao>
        {aoCancelar && (
          <button type="button" onClick={aoCancelar} className="text-sm text-slate-600 underline">
            cancelar
          </button>
        )}
      </div>
    </form>
  )
}

function dataLonga(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { day: "numeric", month: "long" })
}

function dataCurta(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
}

function diaDaSemana(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")
}
